import { colors } from "@indeed/ifl-tokens";
import { type CSSProperties, useCallback, useState } from "react";
import { StyleSheet } from "react-native";

import { Box } from "../../../../mads";
import { getSizeStyles } from "./Button";
// Web variant of Chip: a real `<input type="radio">` in a `<label>` so chips sharing a `groupName`
// get native radio-group keyboard behavior (arrow-key selection + single tab stop) that the native
// Chip's Pressable can't provide on web. Restores the former onboarding `ChipRadio.web`.
import type { ChipProps } from "./Chip.types";
import { Label } from "./Label";

const spacingX = { sm: 0, md: 8, lg: 12 };
const spacingY = { sm: 0, md: 1, lg: 3 };
const labelSizes: { [key: string]: "sm" | "md" } = { sm: "sm", md: "md", lg: "md" };

/**
 * @see https://experience.indeed.design/components/ids/chip
 *
 * The `<label>` MUST wrap the `<input type="radio">` — do not split them. The input is visually
 * hidden (see `hiddenRadioInputStyle`) while the `<Box>` renders the pill.
 */
export function Chip({
  disabled = false,
  invalid = false,
  label,
  value,
  size = "md",
  checked,
  onSelect,
  accessibilityHint,
  buttonRef,
  testID,
  groupName,
}: ChipProps) {
  const [isFocusVisible, setIsFocusVisible] = useState(false);

  const borderColor = invalid
    ? colors.border.status.critical.strong
    : colors.border.action["[base]"];
  const backgroundColor = checked
    ? colors.background.action.inverse["[base]"]
    : colors.background.action.weaker["[base]"];
  const color = checked ? colors.foreground.inverse["[base]"] : colors.foreground.strong;

  const select = useCallback(() => {
    if (!checked) {
      onSelect(value);
    }
  }, [checked, onSelect, value]);

  return (
    <label style={disabled ? disabledLabelStyle : labelStyle}>
      <input
        ref={(el) => {
          if (buttonRef) {
            // buttonRef is typed for the native Chip; on web, point it at the real radio input so
            // focus helpers land on it.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
            (buttonRef as unknown as { current: HTMLInputElement | null }).current = el;
          }
        }}
        type="radio"
        name={groupName}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          if (e.target.checked) {
            select();
          }
        }}
        onFocus={(event) => setIsFocusVisible(event.currentTarget.matches(":focus-visible"))}
        onKeyUp={(event) => setIsFocusVisible(event.currentTarget.matches(":focus-visible"))}
        onBlur={() => setIsFocusVisible(false)}
        aria-label={accessibilityHint ? `${label}, ${accessibilityHint}` : label}
        data-testid={testID}
        style={hiddenRadioInputStyle}
      />
      {/*
       * Mirror the native Chip's structure: the native variant wraps the IFL `<Button>` (which owns
       * the pill's padding + min height via `getSizeStyles`) around an inner `<Box>` carrying
       * `spacingX`/`spacingY`. The web variant has no Button, so apply `getSizeStyles` to the pill
       * directly — without it `sm` chips get zero padding (spacingX.sm === 0) and look cramped.
       */}
      <Box
        aria-hidden
        testID={testID ? `${testID}-pill` : undefined}
        sx={[
          {
            backgroundColor,
            overflow: "hidden",
            borderWidth: 1,
            borderStyle: "solid",
            borderRadius: 9999,
            borderColor,
            flexDirection: "row",
            alignItems: "center",
            ...getSizeStyles(size, false),
          },
          isFocusVisible ? styles.focusRing : undefined,
        ]}
      >
        <Box sx={{ paddingHorizontal: spacingX[size], paddingVertical: spacingY[size] }}>
          <Label disabled={disabled} size={labelSizes[size]} fontWeight="normal" color={color}>
            {label}
          </Label>
        </Box>
      </Box>
    </label>
  );
}

Chip.displayName = "Chip";

/** position: relative anchors the absolutely-positioned hidden input. */
const labelStyle: CSSProperties = {
  position: "relative",
  display: "inline-flex",
  cursor: "pointer",
};

const disabledLabelStyle: CSSProperties = { ...labelStyle, cursor: "not-allowed" };

/**
 * Hidden but accessible: fills the label with near-zero opacity so it stays in the iOS VoiceOver
 * a11y tree (RN-3876) instead of being removed as `display: none` would.
 */
const hiddenRadioInputStyle: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: "100%",
  height: "100%",
  margin: 0,
  padding: 0,
  border: 0,
  opacity: 0.00000001,
  cursor: "inherit",
  // Sit above the visual pill so the radio itself is the click target (the pill would otherwise
  // intercept pointer events / WebDriver clicks). It stays invisible via the near-zero opacity.
  zIndex: 1,
};

const styles = StyleSheet.create({
  focusRing: {
    outlineWidth: 2,
    outlineColor: colors.foreground.primary["[base]"],
    outlineStyle: "solid",
    outlineOffset: 2,
    borderRadius: 9999,
  },
});
